import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://jfi.bc.ca';

const SEO = ({
  title,
  description,
  type = 'website',
  schema,
  breadcrumbs,
  faqs,
  ogImage,
  noindex = false,  // New: controls indexing
  canonicalPath = null,  // New: override canonical path if needed
  htmlLang = 'en-CA',    // <html lang> value (e.g. 'en-CA' or 'pt-BR')
  alternates = null,     // hreflang alternates: [{ hreflang, href }]
  article = null         // BlogPosting metadata: { author, authorUrl, datePublished, dateModified, image, section }
}) => {
  const location = useLocation();
  const defaultDescription = 'John Fleming Insurance - Local insurance brokerage in Burnaby, BC since 1985. Auto, home, business, travel, life, and marine insurance.';
  const siteTitle = title
    ? (title.includes('|') ? title : `${title} | John Fleming Insurance, Burnaby BC`)
    : 'John Fleming Insurance | Burnaby BC Insurance Broker';
  const metaDescription = description || defaultDescription;
  
  // Generate canonical URL - always absolute, self-referencing
  const currentPath = canonicalPath || location.pathname;
  const canonicalUrl = `${SITE_URL}${currentPath}`;

  // Global Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    "name": "John Fleming Insurance",
    "url": SITE_URL,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "6696 Hastings St",
      "addressLocality": "Burnaby",
      "addressRegion": "BC",
      "postalCode": "V5B 1S3",
      "addressCountry": "CA"
    },
    "telephone": "+1-604-298-1218",
    "areaServed": {
      "@type": "Place",
      "name": "British Columbia, Canada"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 49.2267,
        "longitude": -122.9832
      },
      "geoRadius": "500 km"
    }
  };

  // FAQ Schema (if FAQs provided)
  const faqSchema = faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Breadcrumb Schema (if breadcrumbs provided)
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${SITE_URL}${crumb.href}`
    }))
  } : null;

  // BlogPosting Schema (if article metadata provided)
  const blogPostingSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": metaDescription,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "inLanguage": htmlLang,
    "datePublished": article.datePublished,
    ...(article.dateModified ? { "dateModified": article.dateModified } : {}),
    "author": {
      "@type": "Person",
      "name": article.author,
      ...(article.authorUrl ? { "url": article.authorUrl.startsWith('http') ? article.authorUrl : `${SITE_URL}${article.authorUrl}` } : {})
    },
    "publisher": {
      "@type": "Organization",
      "name": "John Fleming Insurance",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/og-default.png`
      }
    },
    "image": article.image || `${SITE_URL}/og-default.png`,
    ...(article.section ? { "articleSection": article.section } : {})
  } : null;

  // Combine all schemas
  const allSchemas = [
    organizationSchema,
    schema,
    faqSchema,
    breadcrumbSchema,
    blogPostingSchema
  ].filter(Boolean);

  const robotsContent = noindex ? 'noindex, follow' : 'index, follow';

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{siteTitle}</title>
      <meta name="description" content={metaDescription} />
      
      {/* Canonical tag - always present, absolute URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang alternates (only emitted when a translation pair exists) */}
      {alternates && alternates.map((alt) => (
        <link
          key={`alt-${alt.hreflang}`}
          rel="alternate"
          hrefLang={alt.hreflang}
          href={alt.href}
        />
      ))}
      
      {/* Robots meta - controls indexing */}
      <meta name="robots" content={robotsContent} />
      
      {/* Open Graph */}
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="John Fleming Insurance" />
      <meta property="og:image" content={ogImage || `${SITE_URL}/og-default.png`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage || `${SITE_URL}/og-default.png`} />
      
      {/* Structured Data / JSON-LD */}
      {allSchemas.map((schemaItem, index) => (
        <script
          key={`schema-${index}`}
          type="application/ld+json"
        >
          {JSON.stringify(schemaItem)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
